import { useMemo } from 'react';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer';

import { getRendererInlineCommentOptions } from '@confluence/annotation-provider-inline-comments/entry-points/getRendererInlineCommentOptions.ts';
import {
	useAnnotationsDispatch,
	useAnnotationManager,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { useIsHighlightOptionPanelEnabled } from '@confluence/comments-util/entry-points/useIsHighlightOptionPanelEnabled.ts';
import { useActiveInlineData } from '@confluence/experiment-inline-reactions/entry-points/useActiveInlineData.ts';
import { useInlineReactionMarkers } from '@confluence/experiment-inline-reactions/entry-points/useInlineReactionMarkers.ts';

export type RendererAnnotationProviderOptions = {
	pageId: string;
	validAnnotationsCallback: (validAnnotations: string[]) => void;
	triggerAnalyticsEvent: any;
	updateDocument: (adfDoc: JSONDocNode) => void;
	isArchived?: boolean;
};

export const useRendererAnnotationProviderOptions = ({
	pageId,
	validAnnotationsCallback,
	triggerAnalyticsEvent,
	updateDocument,
	isArchived,
}: RendererAnnotationProviderOptions) => {
	const { setSelectionProps, setViewProps, setHoverProps } = useAnnotationsDispatch();
	const { isHighlightOptionPanelEnabled } = useIsHighlightOptionPanelEnabled();
	const annotationManager = useAnnotationManager();
	const { reactionMarkerRefs, reactionMarkerMap } = useActiveInlineData(pageId);
	const { mergedReactionMarkerRefs, mergedReactionMarkerMap } = useInlineReactionMarkers({
		reactionMarkerRefs,
		reactionMarkerMap,
	});

	return useMemo(
		() => ({
			inlineComment: getRendererInlineCommentOptions({
				pageId,
				validAnnotationsCallback,
				triggerAnalyticsEvent,
				setSelectionProps,
				setHoverProps,
				setViewProps,
				updateDocument,
				isArchived,
				isHighlightOptionPanelEnabled,
				reactionMarkerRefs: mergedReactionMarkerRefs,
				reactionMarkerMap: mergedReactionMarkerMap,
			}),
			annotationManager,
		}),
		[
			pageId,
			validAnnotationsCallback,
			triggerAnalyticsEvent,
			setSelectionProps,
			setHoverProps,
			setViewProps,
			updateDocument,
			isArchived,
			isHighlightOptionPanelEnabled,
			annotationManager,
			mergedReactionMarkerRefs,
			mergedReactionMarkerMap,
		],
	);
};
